import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';
import Swal from 'sweetalert2';
import './TeamManagement.css';

/* ─── Portal Tooltip (same as Dashboard) ─── */
const Tooltip = ({ text, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, pos: 'top' });

  const show = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = rect.left + rect.width / 2;
    const top  = position === 'bottom' ? rect.bottom + 10 : rect.top - 10;
    setCoords({ top, left, pos: position === 'bottom' ? 'bottom' : 'top' });
    setVisible(true);
  }, [position]);

  const hide = useCallback(() => setVisible(false), []);

  const child = React.Children.only(children);
  const enhanced = React.cloneElement(child, {
    onMouseEnter: (e) => { show(e); child.props.onMouseEnter?.(e); },
    onMouseLeave: (e) => { hide();  child.props.onMouseLeave?.(e); },
    onFocus:      (e) => { show(e); child.props.onFocus?.(e); },
    onBlur:       (e) => { hide();  child.props.onBlur?.(e); },
  });

  return (
    <>
      {enhanced}
      {visible && ReactDOM.createPortal(
        <div className={`tm-tooltip tm-tooltip-${coords.pos}`}
          style={{ top: coords.top, left: coords.left }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

function TeamManagement() {
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  const [expandedCreateForm, setExpandedCreateForm] = useState(false);
  const [expandedAddUsers, setExpandedAddUsers] = useState(new Set());
  
  // Calculate stats
  const totalTeams = teams.length;
  const totalMembers = teams.reduce((sum, team) => sum + (team.member_count || 0), 0);
  const totalOrgUsers = orgUsers.length;
  const collaboratedTeams = teams.filter(t => t.is_collaborator && !t.is_owner).length;
  
  // Modal states
  const [showCollaborationModal, setShowCollaborationModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [currentTeamName, setCurrentTeamName] = useState('');
  const [pendingRemoval, setPendingRemoval] = useState(null);
  
  // Form states
  const [teamName, setTeamName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  const [currentCollaborators, setCurrentCollaborators] = useState([]);
  const [availableCollaborators, setAvailableCollaborators] = useState([]);
  
  // Search states
  const [teamUserSearch, setTeamUserSearch] = useState({});
  const [orgUserSearch, setOrgUserSearch] = useState('');

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get('/qurator/teams');
      
      if (response.data.success) {
        setTeams(response.data.teams);
        setOrgUsers(response.data.org_users);
        setAvailableTeams(response.data.available_teams);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load teams'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const toggleCreateForm = () => {
    setExpandedCreateForm(!expandedCreateForm);
    if (expandedCreateForm) {
      setTeamName('');
    }
  };

  const toggleTeam = (teamId) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
    } else {
      newExpanded.add(teamId);
    }
    setExpandedTeams(newExpanded);
  };

  const toggleAddUsers = (teamId) => {
    const newExpanded = new Set(expandedAddUsers);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
      setSelectedUsers([]);
      setTeamUserSearch({ ...teamUserSearch, [teamId]: '' });
    } else {
      newExpanded.add(teamId);
    }
    setExpandedAddUsers(newExpanded);
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    
    if (!teamName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please enter a team name'
      });
      return;
    }

    try {
      const response = await API.post('/qurator/teams', {
        team_name: teamName.trim()
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        setExpandedCreateForm(false);
        setTeamName('');
        fetchTeams();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to create team'
      });
    }
  };

  const handleAddUsersToTeam = async (e, teamId) => {
    e.preventDefault();
    
    if (selectedUsers.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please select at least one user'
      });
      return;
    }

    try {
      const response = await API.post(`/qurator/teams/${teamId}/users`, {
        selected_users: selectedUsers
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        toggleAddUsers(teamId);
        setSelectedUsers([]);
        fetchTeams();
      } else {
        Swal.fire({
          icon: 'info',
          title: 'Info',
          text: response.data.message
        });
        toggleAddUsers(teamId);
        setSelectedUsers([]);
        fetchTeams();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to add users'
      });
    }
  };

  const handleAddSingleUserToTeam = async (e, userId) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const teamId = parseInt(formData.get('team_id'));

    if (!teamId) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please select a team'
      });
      return;
    }

    try {
      const response = await API.post(`/qurator/teams/${teamId}/users`, {
        user_id: userId
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        fetchTeams();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to add user'
      });
    }
  };

  const handleToggleUser = (userId) => {
    const newSelected = [...selectedUsers];
    const index = newSelected.indexOf(userId);
    if (index > -1) {
      newSelected.splice(index, 1);
    } else {
      newSelected.push(userId);
    }
    setSelectedUsers(newSelected);
  };

  const openRemoveModal = (teamId, userId) => {
    setPendingRemoval({ teamId, userId });
    setShowConfirmModal(true);
  };

  const handleRemoveMember = async () => {
    if (!pendingRemoval) return;

    try {
      const response = await API.delete(`/qurator/teams/${pendingRemoval.teamId}/users/${pendingRemoval.userId}`);

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        setShowConfirmModal(false);
        setPendingRemoval(null);
        fetchTeams();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to remove user'
      });
    }
  };

  const handleDeleteTeam = async (teamId) => {
    const result = await Swal.fire({
      title: 'Delete Team?',
      html: 'Are you sure you want to delete this team?<br><br>This action will:<br>• Remove all members from the team<br>• Cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await API.delete(`/qurator/teams/${teamId}`);

        if (response.data.success) {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: response.data.message,
            timer: 2000,
            showConfirmButton: false
          });
          fetchTeams();
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete team'
        });
      }
    }
  };

  const openCollaborationModal = async (teamId, teamName) => {
    setCurrentTeamId(teamId);
    setCurrentTeamName(teamName);
    setShowCollaborationModal(true);
    setSelectedCollaborators([]);
    
    try {
      const response = await API.get(`/qurator/teams/${teamId}/collaborators`);
      if (response.data.success) {
        setCurrentCollaborators(response.data.current);
        setAvailableCollaborators(response.data.available);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load collaborators'
      });
    }
  };

  const handleAddCollaborators = async (e) => {
    e.preventDefault();

    if (selectedCollaborators.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please select at least one collaborator'
      });
      return;
    }

    try {
      const response = await API.post(`/qurator/teams/${currentTeamId}/collaborators`, {
        selected_collaborators: selectedCollaborators
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        setSelectedCollaborators([]);
        openCollaborationModal(currentTeamId, currentTeamName); // Reload
        fetchTeams();
      } else {
        Swal.fire({
          icon: 'info',
          title: 'Info',
          text: response.data.message
        });
        setSelectedCollaborators([]);
        openCollaborationModal(currentTeamId, currentTeamName); // Reload
        fetchTeams();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to add collaborators'
      });
    }
  };

  const handleRemoveCollaborator = async (teamId, collaboratorId) => {
    const result = await Swal.fire({
      title: 'Remove Collaborator?',
      text: 'Are you sure you want to remove this collaborator?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await API.delete(`/qurator/teams/${teamId}/collaborators/${collaboratorId}`);

        if (response.data.success) {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: response.data.message,
            timer: 2000,
            showConfirmButton: false
          });
          openCollaborationModal(teamId, currentTeamName); // Reload
          fetchTeams();
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to remove collaborator'
        });
      }
    }
  };

  const handleToggleCollaborator = (collabId) => {
    const newSelected = [...selectedCollaborators];
    const index = newSelected.indexOf(collabId);
    if (index > -1) {
      newSelected.splice(index, 1);
    } else {
      newSelected.push(collabId);
    }
    setSelectedCollaborators(newSelected);
  };

  const filterTeamUsers = (teamId, searchText) => {
    setTeamUserSearch({ ...teamUserSearch, [teamId]: searchText });
  };

  const filterOrgUsers = (searchText) => {
    setOrgUserSearch(searchText);
  };

  const getFilteredTeamUsers = (teamId) => {
    const searchText = (teamUserSearch[teamId] || '').toLowerCase();
    if (!searchText) return orgUsers.filter(user => {
      // Only show users not already in team
      const team = teams.find(t => t.group_id === teamId);
      return team && !team.members?.some(m => m.user_id === user.user_id);
    });
    
    return orgUsers.filter(user => {
      const team = teams.find(t => t.group_id === teamId);
      const isMember = team && team.members?.some(m => m.user_id === user.user_id);
      if (isMember) return false;
      
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      return fullName.includes(searchText) || email.includes(searchText);
    });
  };

  const getFilteredOrgUsers = () => {
    const searchText = orgUserSearch.toLowerCase();
    if (!searchText) return orgUsers;
    
    return orgUsers.filter(user => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      return fullName.includes(searchText) || email.includes(searchText);
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getInitials = (firstName, lastName) => {
    return (firstName?.[0] || lastName?.[0] || 'U').toUpperCase();
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="teams-loading">
          <div className="qrt-loading"></div>
          <p>Loading teams...</p>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="tm-page">
        
        {/* ── Hero ── */}
        <div className="tm-hero mb-4">
          <div className="tm-hero-inner">
            <div>
              <h1 className="tm-hero-title">
                <i className="bi bi-people-fill"></i> Team Management
              </h1>
              <p className="tm-hero-sub">
                Create teams, manage users, and assign projects across your organization
              </p>
            </div>
            <i className="bi bi-diagram-3-fill tm-hero-bg-icon d-none d-lg-block"></i>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="tm-stats-row">
          <Tooltip text="Total teams in your organization" position="bottom">
            <div className="tm-stat tm-stat-orange">
              <div className="tm-stat-ico"><i className="bi bi-people-fill"></i></div>
              <div>
                <div className="tm-stat-number">{totalTeams}</div>
                <div className="tm-stat-lbl">Total Teams</div>
              </div>
            </div>
          </Tooltip>
          <Tooltip text="Total members across all teams" position="bottom">
            <div className="tm-stat tm-stat-blue">
              <div className="tm-stat-ico"><i className="bi bi-person-badge-fill"></i></div>
              <div>
                <div className="tm-stat-number">{totalMembers}</div>
                <div className="tm-stat-lbl">Total Members</div>
              </div>
            </div>
          </Tooltip>
          <Tooltip text="Users available in your organization" position="bottom">
            <div className="tm-stat tm-stat-green">
              <div className="tm-stat-ico"><i className="bi bi-person-lines-fill"></i></div>
              <div>
                <div className="tm-stat-number">{totalOrgUsers}</div>
                <div className="tm-stat-lbl">Org Users</div>
              </div>
            </div>
          </Tooltip>
          <Tooltip text="Teams you are collaborating on" position="bottom">
            <div className="tm-stat tm-stat-purple">
              <div className="tm-stat-ico"><i className="bi bi-diagram-3-fill"></i></div>
              <div>
                <div className="tm-stat-number">{collaboratedTeams}</div>
                <div className="tm-stat-lbl">Collaborated</div>
              </div>
            </div>
          </Tooltip>
        </div>

        {/* ── Section Heading ── */}
        <div className="tm-section-heading mb-3">
          <i className="bi bi-lightning-charge-fill"></i>
          <span>Manage Your Teams</span>
        </div>

        {/* Main Layout */}
        <div className={`main-layout ${orgUsers.length === 0 ? 'no-users' : ''}`}>
          {/* Teams Section */}
          <div className="teams-section">
            <div className="teams-section-title">
              <span><i className="bi bi-people-fill"></i> Organization Teams</span>
              <span className="teams-section-badge">{teams.length} Teams</span>
            </div>
            <div className="teams-content">
              {/* Create Team Section */}
              <div className="create-team-section">
                {!expandedCreateForm ? (
                  <>
                    <div className="create-team-info">
                      <h6><i className="bi bi-plus-circle me-2"></i>Create New Team</h6>
                      <small>Build teams to organize users in your organization</small>
                    </div>
                    <div className="create-team-button-wrapper">
                      <Tooltip text="Create a new team for your organization">
                        <button 
                          className="btn btn-primary" 
                          onClick={toggleCreateForm}
                          aria-expanded={expandedCreateForm}
                        >
                          <i className="bi bi-plus-lg me-1"></i>Create Team
                        </button>
                      </Tooltip>
                    </div>
                  </>
                ) : (
                  <div className={`create-team-form ${expandedCreateForm ? 'show' : ''}`} id="createTeamForm">
                    <form onSubmit={handleCreateTeam}>
                      <div className="form-group">
                        <div className="form-control-wrapper">
                          <label className="form-label">Team Name</label>
                          <input 
                            type="text" 
                            name="team_name" 
                            className="form-input" 
                            placeholder="Enter team name..." 
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                            required 
                          />
                        </div>
                        <div className="form-actions">
                          <button type="submit" className="btn btn-success btn-sm">
                            <i className="bi bi-check-lg me-1"></i>Create
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-secondary btn-sm" 
                            onClick={toggleCreateForm}
                          >
                            <i className="bi bi-x-lg me-1"></i>Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Teams List */}
              {teams.length > 0 ? (
                teams.map(team => {
                  const isCollaboratedTeam = team.is_collaborator && !team.is_owner;
                  const canModify = !isCollaboratedTeam; // Simplified - in real app, check org code
                  
                  return (
                    <div key={team.group_id} className={`team-card ${isCollaboratedTeam ? 'collaborated-team' : ''}`}>
                      <div className="team-header">
                        <h6 className="team-name">{team.name}</h6>
                        <div className="team-meta">
                          <span className="meta-item">
                            <i className="bi bi-calendar3"></i>
                            <span>{formatDate(team.created_at)}</span>
                          </span>
                          <span className="meta-separator">•</span>
                          <span className="meta-item">
                            <i className="bi bi-people"></i>
                            <span>{team.member_count} Members</span>
                          </span>
                          <span className="meta-separator">•</span>
                          <span className={`creator-badge ${isCollaboratedTeam ? 'collaborator-badge' : ''}`}>
                            {team.is_owner 
                              ? 'Your Team' 
                              : isCollaboratedTeam
                              ? `Collaborated - by ${team.creator_name} ${team.creator_name1}`
                              : `by ${team.creator_name} ${team.creator_name1}`
                            }
                          </span>
                        </div>
                        <div className="team-actions">
                          <Tooltip text="Assign a project to this team">
                            <Link 
                              to={`/calendar?team_id=${team.group_id}&open_modal=true`}
                              className="btn btn-warning btn-sm"
                            >
                              <i className="bi bi-calendar-check"></i>
                              <span className="btn-assign-text ms-1">Assign Project</span>
                            </Link>
                          </Tooltip>
                          {team.is_owner && (
                            <>
                              <Tooltip text="Manage team collaborators">
                                <button 
                                  className="btn-collaborate" 
                                  onClick={() => openCollaborationModal(team.group_id, team.name)}
                                >
                                  <i className="bi bi-diagram-3"></i>
                                </button>
                              </Tooltip>
                              <Tooltip text="Delete this team">
                                <button 
                                  className="btn btn-danger btn-sm" 
                                  onClick={() => handleDeleteTeam(team.group_id)}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip text={expandedTeams.has(team.group_id) ? "Collapse team details" : "Expand team details"}>
                            <button 
                              className={`expand-btn ${expandedTeams.has(team.group_id) ? 'expanded' : ''}`}
                              onClick={() => toggleTeam(team.group_id)}
                              aria-expanded={expandedTeams.has(team.group_id)}
                            >
                              <i className={`bi bi-chevron-${expandedTeams.has(team.group_id) ? 'up' : 'down'}`}></i>
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                      
                      <div className={`team-body ${expandedTeams.has(team.group_id) ? 'show' : ''}`} id={`team-body-${team.group_id}`}>
                        {/* Add Users Section */}
                        {canModify && (
                          <div className="add-users-section">
                            <div className="add-users-header">
                              <div className="add-users-info">
                                <h6><i className="bi bi-person-plus me-2"></i>Add Users to Team</h6>
                                <small>Select users and add them to this team</small>
                              </div>
                              <Tooltip text="Add users to this team">
                                <button 
                                  className="btn btn-outline btn-sm" 
                                  onClick={() => toggleAddUsers(team.group_id)}
                                  aria-expanded={expandedAddUsers.has(team.group_id)}
                                >
                                  <i className="bi bi-plus"></i> Add Users
                                </button>
                              </Tooltip>
                            </div>
                            
                            <div className={`add-users-form ${expandedAddUsers.has(team.group_id) ? 'show' : ''}`} id={`add-users-${team.group_id}`}>
                              <form onSubmit={(e) => handleAddUsersToTeam(e, team.group_id)}>
                                {/* Search Bar */}
                                <div className="search-bar-container">
                                  <i className="bi bi-search search-icon"></i>
                                  <input 
                                    type="text" 
                                    className="search-bar" 
                                    placeholder="Search users..." 
                                    value={teamUserSearch[team.group_id] || ''}
                                    onChange={(e) => filterTeamUsers(team.group_id, e.target.value)}
                                  />
                                </div>
                                
                                <div className="users-dropdown" id={`users-list-${team.group_id}`}>
                                  {getFilteredTeamUsers(team.group_id).length > 0 ? (
                                    getFilteredTeamUsers(team.group_id).map(user => {
                                      const userFullName = `${user.first_name} ${user.last_name}`.trim();
                                      return (
                                        <div 
                                          key={user.user_id} 
                                          className="user-checkbox"
                                          data-user-name={userFullName.toLowerCase()}
                                          data-user-email={(user.email || '').toLowerCase()}
                                        >
                                          <input 
                                            type="checkbox" 
                                            name="selected_users[]" 
                                            value={user.user_id}
                                            id={`user-${team.group_id}-${user.user_id}`}
                                            checked={selectedUsers.includes(user.user_id)}
                                            onChange={() => handleToggleUser(user.user_id)}
                                          />
                                          <div className="user-info">
                                            <div className="user-avatar">
                                              {getInitials(user.first_name, user.last_name)}
                                            </div>
                                            <div className="user-details">
                                              <div className="user-name">{userFullName}</div>
                                              <div className="user-email">{user.email || 'No email'}</div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div className="text-center text-muted p-3">
                                      <i className="bi bi-check-circle fs-3 d-block mb-2"></i>
                                      All users are already in this team
                                    </div>
                                  )}
                                </div>
                                
                                {getFilteredTeamUsers(team.group_id).length > 0 && (
                                  <div className="form-actions">
                                    <button type="submit" className="btn btn-success btn-sm">
                                      <i className="bi bi-person-plus me-1"></i>Add Selected
                                    </button>
                                    <button 
                                      type="button" 
                                      className="btn btn-secondary btn-sm" 
                                      onClick={() => toggleAddUsers(team.group_id)}
                                    >
                                      <i className="bi bi-x-lg me-1"></i>Cancel
                                    </button>
                                  </div>
                                )}
                              </form>
                            </div>
                          </div>
                        )}

                        {/* Team Members */}
                        <div className="team-members">
                          {team.members && team.members.length > 0 ? (
                            <>
                              <h6><i className="bi bi-people me-2"></i>Team Members ({team.members.length})</h6>
                              {team.members.map(member => {
                                const memberFullName = `${member.first_name} ${member.last_name}`.trim();
                                return (
                                  <div key={member.user_id} className="member-item">
                                    <div className="member-info">
                                      <div className="member-avatar">
                                        {getInitials(member.first_name, member.last_name)}
                                      </div>
                                      <div className="member-details">
                                        <div className="member-name">{memberFullName}</div>
                                        <div className="member-email">{member.email || 'No email'}</div>
                                      </div>
                                    </div>
                                    <div className="member-actions">
                                      <Tooltip text="Assign a project to this member">
                                        <Link 
                                          to={`/calendar?team_id=${team.group_id}&user_id=${member.user_id}&open_modal=true`}
                                          className="btn btn-warning btn-sm"
                                        >
                                          <i className="bi bi-calendar-check"></i>
                                          <span className="d-none d-sm-inline ms-1">Assign</span>
                                        </Link>
                                      </Tooltip>
                                      {canModify && (
                                        <Tooltip text="Remove this member from the team">
                                          <button 
                                            className="remove-btn" 
                                            onClick={() => openRemoveModal(team.group_id, member.user_id)}
                                          >
                                            <i className="bi bi-x-lg"></i>
                                          </button>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            <div className="empty-teams">
                              <i className="bi bi-people"></i>
                              <h6>No members yet</h6>
                              <p>{canModify ? 'Use "Add Users" above to add members to this team' : 'This team has no members assigned yet'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <i className="bi bi-people-fill"></i>
                  <h5>No Teams in Organization</h5>
                  <p>Create your first team to start organizing users in your organization</p>
                </div>
              )}
            </div>
          </div>

          {/* Available Users Section */}
          {orgUsers.length > 0 && (
            <div className="users-section">
              <div className="users-header">
                <h5><i className="bi bi-person-lines-fill"></i> Organization Users</h5>
                <small>{orgUsers.length} users available</small>
              </div>
              
              <div className="users-content">
                {/* Search Bar */}
                <div className="search-bar-container">
                  <i className="bi bi-search search-icon"></i>
                  <input 
                    type="text" 
                    className="search-bar" 
                    placeholder="Search organization users..." 
                    value={orgUserSearch}
                    onChange={(e) => filterOrgUsers(e.target.value)}
                  />
                </div>
                
                <div id="org-users-list">
                  {getFilteredOrgUsers().map(user => {
                    const userFullName = `${user.first_name} ${user.last_name}`.trim();
                    // Get teams where user is NOT a member
                    const availableTeamsForUser = availableTeams.filter(team => {
                      const teamData = teams.find(t => t.group_id === team.group_id);
                      return teamData && !teamData.members?.some(m => m.user_id === user.user_id);
                    });
                    
                    return (
                      <div 
                        key={user.user_id} 
                        className="user-item"
                        data-user-name={userFullName.toLowerCase()}
                        data-user-email={(user.email || '').toLowerCase()}
                      >
                        <div className="user-info">
                          <div className="user-avatar">
                            {getInitials(user.first_name, user.last_name)}
                          </div>
                          <div className="user-details">
                            <div className="user-name">{userFullName}</div>
                            <div className="user-email">{user.email || 'No email'}</div>
                          </div>
                        </div>
                        
                        <form 
                          method="POST" 
                          className="add-to-team-controls"
                          onSubmit={(e) => handleAddSingleUserToTeam(e, user.user_id)}
                        >
                          <input type="hidden" name="user_id" value={user.user_id} />
                          <select name="team_id" className="team-select" required>
                            <option value="">Select Team</option>
                            {availableTeamsForUser.map(team => (
                              <option key={team.group_id} value={team.group_id}>
                                {team.name}
                              </option>
                            ))}
                          </select>
                          <Tooltip text={availableTeamsForUser.length === 0 ? 'User is already in all teams' : 'Add this user to the selected team'}>
                            <button 
                              type="submit" 
                              className="btn btn-success btn-sm" 
                              disabled={availableTeamsForUser.length === 0}
                            >
                              <i className="bi bi-plus-lg me-1"></i>Add to Team
                            </button>
                          </Tooltip>
                        </form>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>{/* end tm-page */}

      {/* Collaboration Modal */}
      {showCollaborationModal && (
        <div className="modal-overlay active" id="collaborationModalOverlay" onClick={() => setShowCollaborationModal(false)}>
          <div className="collaboration-modal" onClick={(e) => e.stopPropagation()}>
            <div className="collaboration-modal-header">
              <h5><i className="bi bi-diagram-3"></i> Manage Collaborators</h5>
              <button className="modal-close-btn" onClick={() => setShowCollaborationModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            
            <div className="collaboration-modal-body">
              <div className="collaborators-list-section">
                <h6><i className="bi bi-people-fill me-2"></i>Current Collaborators</h6>
                <div className="collaborators-list" id="currentCollaboratorsList">
                  {currentCollaborators.length > 0 ? (
                    currentCollaborators.map(collab => (
                      <div key={collab.id} className="collaborator-item">
                        <div className="collaborator-info">
                          <div className="collaborator-avatar">{collab.initial}</div>
                          <div className="collaborator-details">
                            <div className="collaborator-name">{collab.name}</div>
                            <div className="collaborator-email">{collab.email}</div>
                          </div>
                        </div>
                        <button 
                          type="button" 
                          className="remove-collaborator-btn" 
                          onClick={() => handleRemoveCollaborator(currentTeamId, collab.id)}
                        >
                          <i className="bi bi-x-lg me-1"></i>Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="empty-collaborators">
                      <i className="bi bi-people"></i>
                      <p>No collaborators yet</p>
                    </div>
                  )}
                </div>
              </div>
              
              <form onSubmit={handleAddCollaborators} id="collaboratorsForm">
                <div className="add-collaborators-section">
                  <h6><i className="bi bi-person-plus-fill me-2"></i>Add Collaborators</h6>
                  <div className="collaborators-dropdown" id="collaboratorsDropdown">
                    {availableCollaborators.length > 0 ? (
                      availableCollaborators.map(qurator => (
                        <div 
                          key={qurator.id} 
                          className="collaborator-checkbox-item"
                          onClick={() => handleToggleCollaborator(qurator.id)}
                        >
                          <input 
                            type="checkbox" 
                            name="selected_collaborators[]" 
                            value={qurator.id}
                            id={`collab-${qurator.id}`}
                            checked={selectedCollaborators.includes(qurator.id)}
                            onChange={() => handleToggleCollaborator(qurator.id)}
                          />
                          <div className="collaborator-info">
                            <div className="collaborator-avatar">{qurator.initial}</div>
                            <div className="collaborator-details">
                              <div className="collaborator-name">{qurator.name}</div>
                              <div className="collaborator-email">{qurator.email}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-collaborators">
                        <i className="bi bi-person-x"></i>
                        <p>No available curators to add as collaborators</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="collaboration-modal-footer">
                  <button type="button" className="btn btn-cancel" onClick={() => setShowCollaborationModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-add-collaborators">
                    <i className="bi bi-plus-lg me-1"></i>Add Selected
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay active" id="confirmModal" onClick={() => setShowConfirmModal(false)}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>
            <h3 className="modal-title">Remove User from Team?</h3>
            <p className="modal-message">They will no longer have access to team projects.</p>
            <div className="modal-actions">
              <button className="btn btn-cancel-modal" onClick={() => setShowConfirmModal(false)}>
                Cancel
              </button>
              <button className="btn btn-confirm-remove" onClick={handleRemoveMember}>
                Remove User
              </button>
            </div>
          </div>
        </div>
      )}
    </QuratorLayout>
  );
}

export default TeamManagement;
